import { useCallback } from "react";
import { SubmitHandler } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useMutation } from "urql";

import { PageHeader } from "@/components/PageHeader";
import {
    TournamentSeriesForm,
    convertFormToGraphQL,
} from "@/components/forms/TournamentSeriesForm";
import { TournamentSeriesInput } from "@/components/forms/validation/tournament-series";
import { graphql } from "@/gen";

const createTournamentSeriesMutation = graphql(`
    mutation createTournamentSeries($input: CreateTournamentSeriesInput!) {
        createTournamentSeries(input: $input) {
            id
            title
            standingsInfo
        }
    }
`);

export default function NewTournamentSeriesRoute() {
    const navigate = useNavigate();
    const [_result, createTournamentSeries] = useMutation(createTournamentSeriesMutation);

    const handleSubmit: SubmitHandler<TournamentSeriesInput> = useCallback(
        async data => {
            try {
                const res = await createTournamentSeries({
                    input: convertFormToGraphQL(data),
                });
                if (res.error) {
                    res.error.graphQLErrors.forEach(error => {
                        toast.error("Error creating tournament series", {
                            description: error.message,
                        });
                    });
                } else {
                    toast.success("Tournament series created successfully");
                    navigate(`/tournament-series/${res.data?.createTournamentSeries.id}`);
                }
            } catch (err) {
                toast.error("Request error", { description: String(err) });
            }
        },
        [createTournamentSeries, navigate]
    );

    const defaultValues: TournamentSeriesInput = {
        title: "",
        description: null,
        startAt: new Date(),
        endAt: new Date(),
        heroImageId: null,
        thumbnailImageId: null,
        previousWinnerPlayerId: null,
        isInternalOnly: false,
        isHidden: false,
        standingsInfo: null,
        standingsCalculation: { type: "manual" },
    };

    return (
        <div className="m-4 flex flex-col gap-4">
            <PageHeader title="New tournament series" />
            <TournamentSeriesForm
                onSubmit={handleSubmit}
                defaultValues={defaultValues}
                submitText="Create tournament series"
            />
        </div>
    );
}
